import { createPortal } from "react-dom";
import { Printer } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { formatMoney } from "@/lib/app-context";
import { monthLabel, fmtDate, balanceLabel } from "@/lib/sheet-utils";
import { bonusLabel } from "@/components/BonusTypeSelect";

const Line = ({ label, value, strong }) => (
  <div className={`flex justify-between gap-4 py-2 border-b border-slate-200 ${strong ? "font-bold text-lg" : ""}`}>
    <span>{label}</span><span className="font-mono whitespace-nowrap">{value}</span>
  </div>
);

function Slip({ entry, currency }) {
  const m = (n) => formatMoney(n, currency);
  return (
    <div className="max-w-lg mx-auto text-slate-900">
      <div className="flex justify-between items-end border-b-2 border-slate-900 pb-3">
        <div className="font-heading text-2xl font-extrabold">Payslip</div>
        <div className="text-sm font-semibold">{monthLabel(entry.month)}</div>
      </div>
      <div className="grid grid-cols-2 gap-2 text-sm py-4">
        <div><div className="text-xs text-slate-500">Staff name</div><div className="font-semibold">{entry.staff_name}</div></div>
        <div><div className="text-xs text-slate-500">Agent name</div><div className="font-semibold">{entry.agent_name || "—"}</div></div>
        <div><div className="text-xs text-slate-500">Date of joining</div><div className="font-semibold">{fmtDate(entry.joining_date) || "—"}</div></div>
        <div><div className="text-xs text-slate-500">Days worked</div><div className="font-semibold">{entry.days} / 30</div></div>
      </div>
      <Line label="Monthly salary" value={m(entry.salary)} />
      <Line label={`Earned (${entry.days}/30 days)`} value={m(entry.earned)} />
      <Line label="Balance brought forward" value={m(entry.brought_forward)} />
      <Line label={`Add: ${bonusLabel(entry)}`} value={`+ ${m(entry.bonus || 0)}`} />
      <Line label="Less: Advance" value={`- ${m(entry.advance)}`} />
      <Line label="Less: Deduct" value={`- ${m(entry.deduct)}`} />
      <Line label="Payable salary" value={m(entry.payable)} strong />
      <Line label="Paid" value={m(entry.paid_amount)} />
      <Line label={`Balance carried forward (${balanceLabel(entry.balance)})`} value={m(entry.balance)} strong />
      {entry.settled_amount && entry.balance === 0 ? (
        <div className="mt-3 text-sm font-semibold text-teal-800" data-testid="payslip-settlement">
          Final settlement: {entry.settled_amount < 0 ? `${m(-entry.settled_amount)} received from staff` : `${m(entry.settled_amount)} paid to staff`} on {fmtDate(entry.settled_at.slice(0, 10))}
        </div>
      ) : null}
      {entry.note && <div className="mt-3 text-sm" data-testid="payslip-note"><span className="text-xs text-slate-500">Note: </span>{entry.note}</div>}
      <div className="grid grid-cols-2 gap-10 mt-16 text-xs text-slate-500">
        <div className="border-t border-slate-400 pt-1">Employer signature</div>
        <div className="border-t border-slate-400 pt-1">Staff signature</div>
      </div>
    </div>
  );
}

export function PayslipDialog({ entry, currency, onClose }) {
  return (
    <>
      <Dialog open onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="max-w-xl" data-testid="payslip-dialog">
          <DialogHeader><DialogTitle className="sr-only">Payslip</DialogTitle></DialogHeader>
          <Slip entry={entry} currency={currency} />
          <Button onClick={() => window.print()} className="bg-teal-600 hover:bg-teal-700 mt-2" data-testid="print-payslip-button"><Printer size={16} />Print payslip</Button>
        </DialogContent>
      </Dialog>
      {createPortal(<div id="print-area"><Slip entry={entry} currency={currency} /></div>, document.body)}
    </>
  );
}
